import React, { FC, useEffect, useRef, useState } from "react";
import { Button, DecisionActions } from "@shared/components";
import { blurValidationListeners, formSubmitHandler, schemaValidators, useAppForm } from "@shared/form";
import { formatRelativeTime, getFullName, getGbFormattedDayMonth } from "@shared/utils";
import { PersonSparks } from "@containers/Recognition/interfaces";

// Relative, not the "@containers" alias: jest resolves no path aliases in this repo (config-overrides.js
// overrides webpack only), and a relative import here means this component's own tests need no extra
// mocking for formHelpers.tsx's transitive imports beyond what it already needs -- mirrors
// FlameProgress.tsx's own identical relative-import precedent for the same reason (its own comment).
import { coreValueGlyphLetter } from "../../components/RecognitionCreateContainer/RecognitionForm/formHelpers";
// 923-core-context context/engineering/spark-rewards/phase-55-person-sparks-drawer.md §7 PR3: REUSED, never copied -- the same small leaf module
// DenyRedemptionRequestContainer.tsx's own dialog imports (the schema was moved out of that container so
// neither this component nor that one copies it). Relative, same reasoning as formHelpers above.
import {
  DENY_REASON_MAX_LENGTH,
  DenyReasonFormShape,
  validationSchema,
} from "../../components/DenyReason/denyReasonValidation";
import { flameChipText } from "./flameChip";

// Module-level so an omitted `inFlightIds` prop (every existing caller/test that doesn't exercise
// decisions) doesn't allocate a fresh Set every render.
const EMPTY_IN_FLIGHT_IDS: Set<number> = new Set();
const noop = () => undefined;
// The reason form's default values -- a fresh box always starts blank (it only mounts while a card is
// expanded, so there is nothing to reset between opens).
const reasonDefaultValues: DenyReasonFormShape = { reason: "" };

interface PersonSparksDrawerBodyProps {
  data: PersonSparks;
  /** §7 PR3 / plan's own rule: ✓/✕ follow the pending table's own MANAGE_REDEMPTIONS gate. Optional so
   *  every pre-existing counters/flame/ledger/recognition test above (none of which exercises decisions)
   *  needs no change. */
  hasManageRedemptions?: boolean;
  /** The shared decision in-flight set (`getRedemptionRequestDecisionInFlightIds`) -- both buttons on a
   *  card whose request id is in this set render disabled/busy. */
  inFlightIds?: Set<number>;
  /** ✓ -- dispatches `approveRedemptionRequest.request({ id })`. */
  onApprove?: (id: number) => void;
  /** ✕'s reason box's own Deny -- dispatches `denyRedemptionRequest.request({ id, reason, cb })`; `cb`
   *  collapses the box (called only on success, per the action's own contract). */
  onDeny?: (id: number, reason: string, cb: () => void) => void;
}

// 923-core-context context/engineering/spark-rewards/phase-56-drawer-follow-ups.md #3: both local, design-SVG glyphs (Figma frame 9265:1601) -- the app's
// own "clock" Icon asset is hard-coded teal (#00CCCA) with no colour prop and no mask, and no
// "check-circle" Icon exists at all (§4's own fallback: the two Figma SVGs are the spec). Both use
// `stroke="currentColor"` so the drawer's own SCSS (`color: #7f7d85` on each icon tile) paints them grey,
// matching the design, without a colour prop on either glyph itself.
const ClockGlyph: FC = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <path
      d="M7.5 13.75C10.9518 13.75 13.75 10.9518 13.75 7.5C13.75 4.04822 10.9518 1.25 7.5 1.25C4.04822 1.25 1.25 4.04822 1.25 7.5C1.25 10.9518 4.04822 13.75 7.5 13.75Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.5 3.75V7.5L10 8.75"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckCircleGlyph: FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M7 12.8333C10.2217 12.8333 12.8333 10.2217 12.8333 7C12.8333 3.77834 10.2217 1.16667 7 1.16667C3.77834 1.16667 1.16667 3.77834 1.16667 7C1.16667 10.2217 3.77834 12.8333 7 12.8333Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.25 7L6.41667 8.16667L8.75 5.83333"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface DenyReasonBoxProps {
  requestId: number;
  isDeciding: boolean;
  onSubmit: (reason: string) => void;
  onCancel: () => void;
}

/**
 * One open card's inline reason form. Its own component because a form hook cannot live inside the
 * card `map`; it mounts only while its card is expanded, so every open starts blank.
 */
const DenyReasonBox: FC<DenyReasonBoxProps> = ({ requestId, isDeciding, onSubmit, onCancel }) => {
  const form = useAppForm({
    defaultValues: reasonDefaultValues,
    validators: schemaValidators(validationSchema),
    listeners: blurValidationListeners(),
    onSubmit: ({ value }) => onSubmit(value.reason),
  });

  return (
    <form className="person-sparks-drawer-reason-box" onSubmit={formSubmitHandler(form)}>
      <form.AppField name="reason">
        {(field) => (
          <field.Textarea
            // Item 3: the shared Textarea's own `labelTitle` renders a VISIBLE `<label>` with no `for`/`id`
            // link to the control (Label.tsx has neither), so it does not actually establish an accessible
            // name per the accname spec -- confirmed by DenyRedemptionRequestContainer's own test suite,
            // which never queries its identically-shaped Textarea by role+name. §4's card has no room for a
            // visible label anyway (not in the frame), so `aria-label` -- a prop the shared Textarea already
            // forwards untouched via its own `...inputProps` spread -- is used directly instead, without
            // changing that shared component.
            aria-label="Reason"
            data-testid={`person-sparks-deny-reason-${requestId}`}
            maxLength={DENY_REASON_MAX_LENGTH}
            placeholder="Why is this request being denied?"
          />
        )}
      </form.AppField>
      <div className="person-sparks-drawer-reason-box-footer">
        <Button type="button" className="subtle auto-width" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" className="accent padding margin-left" disabled={isDeciding}>
          Deny
        </Button>
      </div>
    </form>
  );
};

/**
 * The loaded drawer's own body: counters, flame chip, and the three sections (§4), including the OPEN
 * REQUEST card's own ✓/✕ decisions (§7 PR3).
 *
 * @param props.data - the drawer subject's combined snapshot (`PersonSparks`, already loaded).
 * @param props.hasManageRedemptions - gates the ✓/✕ actions, same rule as the pending table's own
 *   Actions column -- omitted entirely (not merely rendered disabled) when false.
 * @param props.inFlightIds - the shared decision in-flight set (`getRedemptionRequestDecisionInFlightIds`);
 *   both buttons on a card whose request id is in this set render disabled/busy.
 * @param props.onApprove - ✓ -- dispatches `approveRedemptionRequest.request({ id })`.
 * @param props.onDeny - the reason box's own Deny -- dispatches
 *   `denyRedemptionRequest.request({ id, reason, cb })`; `cb` collapses that card's own box on success.
 */
const PersonSparksDrawerBody: FC<PersonSparksDrawerBodyProps> = (props) => {
  const {
    data,
    hasManageRedemptions = false,
    inFlightIds = EMPTY_IN_FLIGHT_IDS,
    onApprove = noop,
    onDeny = noop,
  } = props;
  const { summary, flame, recentRecognitions } = data;

  // §7 PR3: one reason box open at a time, not independent per card -- simpler state, and the frame
  // itself only ever shows one open request card at once in practice.
  const [expandedRequestId, setExpandedRequestId] = useState<number | null>(null);

  /**
   * 923-core-context context/engineering/spark-rewards/phase-55-person-sparks-drawer.md §7 PR3 -- mirrors DenyRedemptionRequestContainer.tsx's own "fix
   * pass F" exactly: `inFlightIds` is redux-derived and only reflects a NEW render, so two same-tick
   * submits both read the same stale "not yet in flight" state before React re-renders with the
   * in-flight `disabled` prop. A SINGLE ref, not one per card -- only one box is ever open at once
   * (`expandedRequestId` above) -- set synchronously inside the box's submit handler below, so the second
   * of two same-tick clicks sees it immediately.
   *
   * Reset in two places: whenever a DIFFERENT card's box opens, or the SAME one re-opens after Cancel
   * (a ref left `true` from a previous card closed mid-flight via Cancel must not block a fresh card's
   * first submit); and whenever the OPEN card's own in-flight state clears WITHOUT a success (the
   * in-flight set drops an id on BOTH success and failure -- success already collapses the box via the
   * `cb` below, so clearing while the box is STILL open can only be a failure, and a real retry must not
   * stay blocked by the same-tick guard from the failed attempt).
   */
  const isSubmittingRef = useRef(false);
  const isExpandedRequestDeciding = expandedRequestId !== null && inFlightIds.has(expandedRequestId);

  useEffect(() => {
    isSubmittingRef.current = false;
  }, [expandedRequestId]);

  useEffect(() => {
    if (!isExpandedRequestDeciding) {
      isSubmittingRef.current = false;
    }
  }, [isExpandedRequestDeciding]);

  // D2 (923-core-context context/engineering/spark-rewards/phase-55-person-sparks-drawer.md §3): BEFORE holds, never the api's own hold-adjusted
  // `redeemable` -- see this field's own consequence note in the plan for why the open-request line's
  // "left if approved" below is a DIFFERENT number (summary.redeemable) from this one.
  const toRedeem = summary.lifetimeEarned - summary.redeemed;
  const chip = flameChipText(flame);
  // §4: accessible names mirror the pending table's own ("Approve <reward> for <name>"), fixed to this
  // drawer's one subject.
  const personFullName = getFullName({ first_name: data.firstName, last_name: data.lastName });

  return (
    <>
      <div className="person-sparks-drawer-counters">
        <div className="person-sparks-drawer-counter">
          <span className="person-sparks-drawer-counter-value person-sparks-drawer-counter-value--lifetime">
            {summary.lifetimeEarned}
          </span>
          <span className="person-sparks-drawer-counter-label">Lifetime</span>
        </div>
        <div className="person-sparks-drawer-counter">
          <span className="person-sparks-drawer-counter-value person-sparks-drawer-counter-value--redeemed">
            {summary.redeemed}
          </span>
          <span className="person-sparks-drawer-counter-label">Redeemed</span>
        </div>
        <div className="person-sparks-drawer-counter">
          <span className="person-sparks-drawer-counter-value person-sparks-drawer-counter-value--to-redeem">
            {toRedeem}
          </span>
          <span className="person-sparks-drawer-counter-label">To redeem</span>
        </div>
      </div>

      <div className="person-sparks-drawer-flame-chip">
        <span className="person-sparks-drawer-flame-title">⚡ {chip.title}</span>
        {chip.subtitle ? <span className="person-sparks-drawer-flame-subtitle">{chip.subtitle}</span> : null}
      </div>

      {/* Build-time call (§3): no open request -> the whole section is omitted, not an empty state. */}
      {summary.pendingRequests.length > 0 ? (
        <div className="person-sparks-drawer-section">
          <span className="person-sparks-drawer-section-label">Open request</span>
          {/* Build-time call (§3): several open requests -> one card per request, stacked. */}
          {summary.pendingRequests.map((request) => {
            const isDeciding = inFlightIds.has(request.id);
            return (
              <div className="person-sparks-drawer-request-card" key={request.id}>
                <div className="person-sparks-drawer-request-row">
                  <div className="person-sparks-drawer-request-icon">
                    <ClockGlyph />
                  </div>
                  <div className="person-sparks-drawer-request-text">
                    <div className="person-sparks-drawer-request-title">{request.reward}</div>
                    {/* Consequence of D2 (§3): "left if approved" is the api's OWN hold-adjusted
                        `summary.redeemable` -- the SAME number for every one of this person's pending
                        rows, never a per-request field. */}
                    <div className="person-sparks-drawer-request-meta">
                      {request.points} ⚡ · {formatRelativeTime(request.created_at)} · {summary.redeemable} ⚡ left if
                      approved
                    </div>
                  </div>
                  {/* §4/plan's own rule: gated on MANAGE_REDEMPTIONS, same as the pending table's own
                      Actions column -- omitted entirely for a viewer who lacks it, never rendered
                      disabled. Reuses the shared DecisionActions (the pending table's own ✓/✕), resized to
                      this card's 30px/12px spec via the className below (index.scss). */}
                  {hasManageRedemptions ? (
                    <DecisionActions
                      className="person-sparks-drawer-request-actions"
                      onApprove={() => onApprove(request.id)}
                      onDeny={() => setExpandedRequestId(request.id)}
                      approveAriaLabel={`Approve ${personFullName}'s request for ${request.reward}`}
                      denyAriaLabel={`Deny ${personFullName}'s request for ${request.reward}`}
                      disabled={isDeciding}
                      busy={isDeciding}
                    />
                  ) : null}
                </div>
                {/* D3 (§3): the inline reason box -- never a separate overlay, the drawer never closes. */}
                {expandedRequestId === request.id ? (
                  <DenyReasonBox
                    requestId={request.id}
                    isDeciding={isDeciding}
                    onCancel={() => setExpandedRequestId(null)}
                    onSubmit={(reason) => {
                      // Item 1: same-tick double-submit guard (this component's own top-level comment).
                      if (isSubmittingRef.current) {
                        return;
                      }
                      isSubmittingRef.current = true;
                      onDeny(request.id, reason, () =>
                        // Item 2: a functional update -- a stale closure's plain `setExpandedRequestId(null)`
                        // would collapse whichever card happens to be open AT THE TIME cb fires, which is
                        // not necessarily THIS card if the drawer moved on to a different open request in
                        // the meantime. Only clears the state if it still points at THIS request's id.
                        setExpandedRequestId((current) => (current === request.id ? null : current)),
                      );
                    }}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      <div className="person-sparks-drawer-section">
        <span className="person-sparks-drawer-section-label">Redeemed</span>
        {summary.ledger.length > 0 ? (
          summary.ledger.map((row) => (
            <div className="person-sparks-drawer-ledger-row" key={row.id}>
              <div className="person-sparks-drawer-ledger-icon">
                <CheckCircleGlyph />
              </div>
              <div className="person-sparks-drawer-ledger-text">
                {/* Mirrors orgWideRedemptionLedgerTableProperties.tsx's own decision: reward ?? note,
                    never a blank cell for a legacy row with a null reward. */}
                <div className="person-sparks-drawer-ledger-title">{row.reward ?? row.note ?? "—"}</div>
                <div className="person-sparks-drawer-ledger-meta">
                  {getGbFormattedDayMonth(row.created_at)} · approved by {row.marked_by}
                </div>
              </div>
              <span className="person-sparks-drawer-ledger-points">{row.points} ⚡</span>
            </div>
          ))
        ) : (
          <div className="person-sparks-drawer-empty" data-testid="person-sparks-redeemed-empty">
            Nothing redeemed yet.
          </div>
        )}
      </div>

      <div className="person-sparks-drawer-section">
        <span className="person-sparks-drawer-section-label">Recently recognised for</span>
        {recentRecognitions.length > 0 ? (
          recentRecognitions.map((recognition) => (
            <div className="person-sparks-drawer-recognition-row" key={recognition.id}>
              <div className="person-sparks-drawer-recognition-glyph">
                {recognition.core_value ? coreValueGlyphLetter(recognition.core_value.title) : ""}
              </div>
              <div className="person-sparks-drawer-recognition-text">
                <div className="person-sparks-drawer-recognition-title">{recognition.core_value?.title}</div>
                {/* `created_by` is optional on Recognition -- guard with truthiness, never render
                    "from undefined" (matches the plan's own explicit `?.`/truthiness instruction). */}
                {recognition.created_by ? (
                  <div className="person-sparks-drawer-recognition-from">from {recognition.created_by}</div>
                ) : null}
              </div>
              {/* Fix pass (browser review): the score carries the same "N ⚡" treatment as the REDEEMED
                  row's own points above -- §4 shows "10 ⚡", not a bare number. Reuses the identical
                  "{value} ⚡" literal, not a new component -- there is no separate SparkAmount element in
                  this codebase to import instead (`.person-sparks-drawer-ledger-points`'s own line is
                  the same inline pattern, just a different className for its own colour). */}
              <span className="person-sparks-drawer-recognition-score">{recognition.score} ⚡</span>
            </div>
          ))
        ) : (
          <div className="person-sparks-drawer-empty" data-testid="person-sparks-recognitions-empty">
            No recognitions yet.
          </div>
        )}
      </div>
    </>
  );
};

export default PersonSparksDrawerBody;
